/**
 * Customer notifications (DEV-100) — separate from the assessment.
 *
 * The triage decides; a confirmed product verdict AFFECTED creates one
 * Benachrichtigung per customer and vulnerability case — product × CVE,
 * naming the customer's instances on every affected version (DEV-128,
 * DEV-134). The Security
 * Advisory is written and released once per product in its vulnerability case
 * (DEV-134), the evidence per version in the triage; this page shows them
 * read-only and works through the mails
 * per product version × CVE: review and edit the draft per customer and queue
 * the selected mails — only once the advisory is released. The
 * Postausgang (DEV-93) below sends them: today by hand from the mail client,
 * later through an automatic transport. The ENISA report can dock here later.
 */
// Core `t` is aliased: the components below take their own `t` from useLingui().
import { plural, t as translate } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import Banner from '../components/Banner';
import Chip, { ChipList } from '../components/Chip';
import CveLink, { CveTitleLink } from '../components/CveLink';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import {
  errorMessage, fetchCaseAdvisory, fetchNotificationQueue, fetchOutbox, fetchVulnerabilityEvents,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate } from '../lib/format';
import { readOnlyNotice } from '../lib/permissions';
import type {
  AdvisoryStatus, CaseAdvisory, NotificationQueueRow, NotificationQueueVersion, OutboxRow, VulnerabilityEventRow,
} from '../lib/types';

import MailPreview from './triage/MailPreview';
import { markdownToHtml } from './triage/mailText';
import NotificationDrafts from './triage/NotificationDrafts';
import OutboxPanel from './triage/OutboxPanel';
import { AttachmentsPanel } from './triage/TriageDocumentPanels';
import { useTriageDocuments } from './triage/useTriageDocuments';

import './triage/triage.css';

/** How many customer names a row shows before it says "+ n weitere". */
const MAX_CUSTOMER_NAMES = 3;

/** The customers of a row by name; the full list sits in the tooltip when it is cut. */
function CustomerNames({ names }: Readonly<{ names: readonly string[] }>): ReactNode {
  if (names.length === 0) return <span className="muted"><Trans>keine Kunden</Trans></span>;
  const shown = names.slice(0, MAX_CUSTOMER_NAMES);
  const more = names.length - shown.length;
  return (
    <span title={more > 0 ? names.join('\n') : undefined}>
      {shown.join(', ')}
      {more > 0 ? <span className="muted"> <Trans>+ {more} weitere</Trans></span> : null}
    </span>
  );
}

/** The affected versions; the notified ones when none is affected any more. */
function rowVersions(row: NotificationQueueRow): NotificationQueueVersion[] {
  const affected = row.versions.filter((version) => version.affected);
  return affected.length > 0 ? affected : row.versions;
}

function productLabel(row: NotificationQueueRow): string {
  const versions = rowVersions(row).map((version) => version.version_nr ?? `#${version.version_id}`).join(', ');
  const productTypeId = row.product_type_id;
  return [row.product_name ?? translate`Produkt #${productTypeId}`, versions].filter(Boolean).join(' · ');
}

function caseKey(row: NotificationQueueRow): string {
  return String(row.case_id);
}

/** Evidence of one version, read-only; it is kept per version in the triage. */
function VersionEvidence({ version, cve }: Readonly<{ version: NotificationQueueVersion; cve: string }>): ReactNode {
  const { t } = useLingui();
  const documents = useTriageDocuments({ versionId: version.version_id, cve });
  const versionLabel = version.version_nr ?? version.version_id;
  const title = t`Nachweise · Version ${versionLabel}`;
  const loadError = documents.loadError;
  if (documents.loading) return <p><output><Spinner /> <Trans>{title} werden geladen…</Trans></output></p>;
  if (loadError) return <Banner kind="error"><Trans>{title} konnten nicht geladen werden: {loadError}</Trans></Banner>;
  return documents.documents
    ? <AttachmentsPanel state={documents} disabled={documents.busy} canWrite={false} title={title} />
    : null;
}

/** The component hit the triage dialog opens on. */
function eventFor(row: NotificationQueueRow, events: readonly VulnerabilityEventRow[]): VulnerabilityEventRow | null {
  return events.find((event) => event.cve.id?.toUpperCase() === row.cve
    && event.komponente.id !== null && row.component_version_ids.includes(event.komponente.id))
    ?? events.find((event) => event.cve.id?.toUpperCase() === row.cve)
    ?? null;
}

type WorkspaceTab = 'advisory' | 'drafts';

/** Release state of the advisory; mails are queued only once it is released (DEV-129). */
function AdvisoryChip({ status }: Readonly<{ status: AdvisoryStatus | null }>): ReactNode {
  const { t } = useLingui();
  if (status === 'RELEASED') return <Chip tone="good"><Trans>Freigegeben</Trans></Chip>;
  return <Chip tone="warn">{status === 'IN_PROGRESS' ? t`In Arbeit` : t`Fehlt`}</Chip>;
}

function advisoryRank(status: AdvisoryStatus | null): number {
  if (status === 'RELEASED') return 2;
  return status === 'IN_PROGRESS' ? 1 : 0;
}

/** Open mails can be sent: the advisory they are drafted from is released. */
function readyToNotify(row: NotificationQueueRow): boolean {
  return row.notifications.open > 0 && row.advisory_status === 'RELEASED';
}

/** The case advisory, read-only; it is written and released in the triage (DEV-134). */
function AdvisoryPreview({ advisory, editLink }: Readonly<{ advisory: CaseAdvisory; editLink: string }>): ReactNode {
  const { t } = useLingui();
  const released = advisory.status === 'RELEASED';
  const version = advisory.change_log.at(-1)?.version;
  const releasedAt = fmtDate(advisory.released_at);
  const releasedBy = advisory.released_by;
  return (
    <section className="triage-doc-panel" aria-label={t`Security Advisory (Kunden)`}>
      <div className="triage-panel-heading">
        <h3><Trans>Security Advisory (Kunden)</Trans></h3>
        <AdvisoryChip status={advisory.status} />
      </div>
      {released ? null : (
        <p className="hint">
          {advisory.status ? <Trans>Noch nicht freigegeben.</Trans> : <Trans>Noch kein Security Advisory gespeichert.</Trans>}{' '}
          <Trans>
            Es wird in der Triage einmal je Produkt im Schwachstellenfall verfasst und freigegeben; erst dann lassen
            sich die Kundenmails einreihen.
          </Trans>
        </p>
      )}
      {advisory.status ? (
        <MailPreview html={markdownToHtml(advisory.text)} title={t`Vorschau des Security Advisory`} />
      ) : null}
      <div className="triage-document-toolbar">
        <Link className="link-button" to={editLink}><Trans>In der Triage bearbeiten</Trans></Link>
        {released ? (
          <span className="hint">
            <Trans>Version {version} freigegeben {releasedAt} · {releasedBy}</Trans>
          </span>
        ) : null}
      </div>
    </section>
  );
}

/** Where the mails of a row stand; open ones wait while the advisory is not released. */
function DeliveryCell({ row }: Readonly<{ row: NotificationQueueRow }>): ReactNode {
  const { t } = useLingui();
  const { open, queued, failed, sent } = row.notifications;
  const deadline = row.next_deadline ? fmtDate(row.next_deadline) : null;
  const waiting = row.notifications.open > 0 && row.advisory_status !== 'RELEASED';
  return (
    <div>
      <ChipList>
        {open ? <Chip tone="warn"><Trans>{open} offen</Trans></Chip> : null}
        {queued ? <Chip tone="info"><Trans>{queued} im Postausgang</Trans></Chip> : null}
        {failed ? <Chip tone="alert"><Trans>{failed} fehlgeschlagen</Trans></Chip> : null}
        {sent ? <Chip tone="good"><Trans>{sent} versendet</Trans></Chip> : null}
        {row.contradiction ? <Chip tone="alert" title={t`Keine Version ist mehr als „Betroffen“ bestätigt`}><Trans>Widerspruch</Trans></Chip> : null}
      </ChipList>
      {waiting ? <div className="prod"><Trans>wartet auf die Freigabe des Advisorys</Trans></div> : null}
      {deadline ? <div className="prod" title={t`Nächste offene Frist der Behördenmeldung (CRA Art. 14)`}><Trans>Meldefrist {deadline}</Trans></div> : null}
    </div>
  );
}

/** The advisory of the row's case; a row without a case has none to show. */
function useCaseAdvisory(caseId: number | null): { value: CaseAdvisory | null; error: string | null } {
  const [state, setState] = useState<{ value: CaseAdvisory | null; error: string | null }>({ value: null, error: null });
  useEffect(() => {
    if (caseId === null) {
      setState({ value: null, error: translate`Zu dieser Produktversion gibt es keinen Schwachstellenfall.` });
      return undefined;
    }
    let active = true;
    fetchCaseAdvisory(caseId)
      .then((value) => { if (active) setState({ value, error: null }); })
      .catch((err: unknown) => { if (active) setState({ value: null, error: errorMessage(err) }); });
    return () => { active = false; };
  }, [caseId]);
  return state;
}

function NotificationWorkspace({ row, event, canSend, onClose, onChanged }: Readonly<{
  row: NotificationQueueRow;
  event: VulnerabilityEventRow;
  canSend: boolean;
  onClose: () => void;
  onChanged: () => void;
}>): ReactNode {
  const { t } = useLingui();
  const advisory = useCaseAdvisory(row.case_id);
  const [tab, setTab] = useState<WorkspaceTab>(row.advisory_status === 'RELEASED' ? 'drafts' : 'advisory');
  const [draftsDirty, setDraftsDirty] = useState(false);
  // The triage dialog opens on the case of the product (DEV-134).
  const editLink = `/triage?event=${event.id}&version=${rowVersions(row)[0]?.version_id ?? ''}&tab=case`;

  function requestClose(): void {
    if (draftsDirty && !window.confirm(t`Ungespeicherte Änderungen an den Entwürfen verwerfen?`)) return;
    onClose();
  }

  const tabs: Array<{ id: WorkspaceTab; label: string }> = [
    { id: 'advisory', label: t`Security Advisory` },
    { id: 'drafts', label: t`Entwürfe & Versand` },
  ];
  const advisoryError = advisory.error;
  const customers = t`${plural(row.customer_count, { one: '# Kunde', other: '# Kunden' })}`;
  const instances = t`${plural(row.instance_count, { one: '# Instanz', other: '# Instanzen' })}`;
  const product = productLabel(row);

  return (
    <Modal
      title={<><Trans>Benachrichtigung</Trans> <span className="muted">/</span> <CveTitleLink cve={row.cve} /></>}
      subtitle={`${product} · ${customers} · ${instances}`}
      size="wide"
      onClose={requestClose}
      footer={<><div className="spacer" /><button type="button" className="ghost" onClick={requestClose}><Trans>Schließen</Trans></button></>}
    >
      <div className="triage-workspace">
        <div className="tabs triage-workspace-tabs" role="tablist" aria-label={t`Benachrichtigung`}>
          {tabs.map((item) => (
            <button key={item.id} type="button" role="tab" aria-selected={tab === item.id}
              id={`notification-tab-${item.id}`} aria-controls={`notification-panel-${item.id}`}
              tabIndex={tab === item.id ? 0 : -1} onClick={() => setTab(item.id)}>
              {item.label}
            </button>
          ))}
        </div>
        {row.contradiction ? (
          <Banner kind="error">
            <Trans>Keine Version des Produkts ist mehr als „Betroffen“ bestätigt. Versendete Benachrichtigungen bleiben bestehen.</Trans>
          </Banner>
        ) : null}

        <div role="tabpanel" id="notification-panel-advisory" aria-labelledby="notification-tab-advisory" hidden={tab !== 'advisory'}>
          <div className="assessment-documents">
            {advisoryError ? <Banner kind="error"><Trans>Security Advisory konnte nicht geladen werden: {advisoryError}</Trans></Banner> : null}
            {advisory.value ? <AdvisoryPreview advisory={advisory.value} editLink={editLink} /> : null}
            {advisory.value === null && !advisory.error ? <p><output><Spinner /> <Trans>Security Advisory wird geladen…</Trans></output></p> : null}
            {rowVersions(row).map((version) => (
              <VersionEvidence key={version.version_id} version={version} cve={row.cve} />
            ))}
          </div>
        </div>

        <div role="tabpanel" id="notification-panel-drafts" aria-labelledby="notification-tab-drafts" hidden={tab !== 'drafts'}>
          {tab === 'drafts' && advisory.value ? (
            <NotificationDrafts
              caseId={row.case_id}
              advisoryRevision={advisory.value.revision}
              canSend={canSend}
              onQueued={onChanged}
              advisoryLink={editLink}
              onDirtyChange={setDraftsDirty}
            />
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

export function NotificationsPage(): ReactNode {
  const { t, i18n } = useLingui();
  const { has } = useAuth();
  const [params] = useSearchParams();
  const [rows, setRows] = useState<NotificationQueueRow[] | null>(null);
  const [events, setEvents] = useState<VulnerabilityEventRow[]>([]);
  const [outbox, setOutbox] = useState<OutboxRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [onlyOpen, setOnlyOpen] = useState(true);
  // `?case=` opens the vulnerability case right away (DEV-134).
  const [openKey, setOpenKey] = useState<string | null>(() => params.get('case'));
  const canSend = has('notification.send');

  const load = useCallback(async (): Promise<void> => {
    try {
      const [queue, triage, queued] = await Promise.all([
        // Sent mails too: the Postausgang hides them unless asked to show them.
        fetchNotificationQueue(), fetchVulnerabilityEvents(), fetchOutbox(true)]);
      setRows(queue.rows);
      setEvents(triage.events);
      setOutbox(queued.rows);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (rows ?? []).filter((row) => {
      const pending = row.notifications.open + row.notifications.queued + row.notifications.failed;
      if (onlyOpen && pending === 0 && !row.contradiction && caseKey(row) !== openKey) return false;
      if (!needle) return true;
      return [row.cve, row.product_name, ...row.versions.map((v) => v.version_nr), ...row.components, ...row.customers]
        .some((value) => value?.toLowerCase().includes(needle));
    });
  }, [rows, search, onlyOpen, openKey]);
  const openRow = rows?.find((row) => caseKey(row) === openKey) ?? null;
  const openEvent = openRow ? eventFor(openRow, events) : null;

  const columns = useMemo((): DataTableColumn<NotificationQueueRow>[] => [
    { key: 'cve', header: 'CVE', width: '15%', sortValue: (row) => row.cve, render: (row) => <CveLink cve={row.cve} /> },
    { key: 'product', header: t`Produkt und Versionen`, sortValue: (row) => productLabel(row),
      render: (row) => (
        <div>
          <div className="kunde-name">
            {productLabel(row)} {rowVersions(row).some((v) => v.custom) ? <Chip tone="info"><Trans>Kundenversion</Trans></Chip> : null}
          </div>
          <div className="prod">{row.components.join(', ') || '—'}</div>
        </div>
      ) },
    { key: 'kunden', header: t`Empfänger`, width: '18%', sortValue: (row) => row.customers.join(', '),
      render: (row) => <div><CustomerNames names={row.customers} /><div className="prod"><Plural value={row.instance_count} one="# Instanz" other="# Instanzen" /></div></div> },
    { key: 'advisory', header: t`Security Advisory`, width: '15%', sortValue: (row) => advisoryRank(row.advisory_status),
      render: (row) => <AdvisoryChip status={row.advisory_status} /> },
    { key: 'versand', header: t`Versand`, width: '18%', sortValue: (row) => row.notifications.open,
      render: (row) => <DeliveryCell row={row} /> },
    { key: 'aktion', header: t`Aktion`, width: '12%',
      render: (row) => (
        <div className="triage-actions">
          <button type="button" className={readyToNotify(row) ? 'primary small' : 'ghost small'}
            onClick={(e) => { e.stopPropagation(); setOpenKey(caseKey(row)); }}>
            {readyToNotify(row) ? t`Benachrichtigen` : t`Öffnen`}
          </button>
        </div>
      ) },
    // i18n.locale: the cells carry translated text of module helpers.
  ], [t, i18n.locale]);
  const caseCount = t`${plural(visible.length, { one: '# Schwachstellenfall', other: '# Schwachstellenfälle' })}`;

  if (rows === null && error === null) return <PageSpinner text={t`Benachrichtigungen werden geladen…`} />;

  return (
    <>
      <PageHeader title={t`Kundenbenachrichtigungen`} sub={t`Je Produkt und CVE: eine Mail je Kunde über alle betroffenen Versionen prüfen und versenden – das Security Advisory entsteht in der Triage`} />
      {error ? <Banner kind="error">{error}</Banner> : null}
      {!canSend ? <Banner kind="info">{readOnlyNotice('notification.send')}</Banner> : null}
      <section className="card">
        <div className="card-head table-head"><h2><Trans>Benachrichtigungen</Trans></h2></div>
        <div className="table-toolbar">
          <input type="search" value={search} placeholder={t`CVE, Produkt oder Komponente suchen…`}
            aria-label={t`Benachrichtigungen filtern`} onChange={(e) => setSearch(e.target.value)} />
          <label className="check">
            <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />
            <Trans>Nur offene</Trans>
          </label>
          <div className="table-toolbar-options">
            <span className="table-count" aria-live="polite">{caseCount}</span>
          </div>
        </div>
        {(rows ?? []).length === 0 ? (
          <EmptyState title={t`Keine Benachrichtigungen`}>
            <Trans>Benachrichtigungen entstehen, sobald eine Version eines Produkts in der <Link to="/triage">Triage</Link> als „Betroffen“ bestätigt ist.</Trans>
          </EmptyState>
        ) : (
          <DataTable rows={visible} columns={columns} rowKey={caseKey} pageSize={15} mobileBreakpoint={900}
            caption={t`Kundenbenachrichtigungen je Schwachstellenfall`}
            emptyText={t`Keine offenen Benachrichtigungen. „Nur offene“ abwählen, um alle zu sehen.`} />
        )}
      </section>
      <OutboxPanel rows={outbox} canSend={canSend} onChanged={() => void load()} />
      {openRow && openEvent ? (
        <NotificationWorkspace
          key={caseKey(openRow)}
          row={openRow}
          event={openEvent}
          canSend={canSend}
          onClose={() => setOpenKey(null)}
          onChanged={() => void load()}
        />
      ) : null}
    </>
  );
}

export default NotificationsPage;
